import { useForm, UseFormRegister } from "react-hook-form";
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BaseURL } from "../../../../services/api";
import OTPVerificationModal from "../../../../components/OTPVerificationModal";
import { useSelector } from "react-redux";
import { RootState } from "../../../../store/store";

const FilterType: { [key: string]: string } = {
  counselling: "dropdown",
  state: "dropdown",
  caste: "dropdown",
  gender: "radio",
};

interface FormData {
  rank: string | number;
  mobileNumber: string;
  counselling?: string;
  state?: string;
  caste?: string;
  gender?: string;
  [key: string]: string | number | undefined;
}

interface ICollegeCourse {
  courseTitle: string;
  lastYearCutoffAiRank: number;
  openingRank: number;
  closingRank: number;
  courseName: string;
  collegeId: number;
  fee: string;
  seats: number;
  counsellingName?: string;
  caste?: string;
  gender?: string;
  year: string;
}

interface ICollege {
  collegeName: string;
  ownerShip: string;
  collegeId: number;
  collegeTitle: string;
  courseVo: ICollegeCourse[];
  allCourses: ICollegeCourse[];
  stateName?: string;
}

interface IResponseData {
  toughChanceColleges: number;
  filter: any[];
  goodChanceColleges: number;
  colleges: ICollege[];
  getinChanceColleges: number;
  totalCount: number;
}

interface IApiResponse {
  message: string;
  data: IResponseData;
  timeStamp: string;
}

const PredictionForm = () => {
  const data = useSelector(
    (state: RootState) => state.collegePredictorIntermediate.landingData
  );
  const [collegesData, setCollegesData] = useState<IResponseData | null>(null);
  const [fullCollegesData, setFullCollegesData] =
    useState<IResponseData | null>(null);
  const [counselling, setCounselling] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [caste, setCaste] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);

  const [showOtpModal, setShowOtpModal] = useState(false);
  const [formDataToSubmit, setFormDataToSubmit] = useState<FormData | null>(
    null
  );
  const [verifiedPhoneNumber, setVerifiedPhoneNumber] = useState<string>("");

  const { isAuthenticated, user } = useSelector(
    (state: RootState) => state.auth
  );

  const navigate = useNavigate();
  const { exam } = useParams();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      rank: "",
      mobileNumber: "",
      counselling: "",
      state: "",
      caste: "",
      gender: "",
    },
  });

  const mobileNumber = watch("mobileNumber");

  // Get user's phone number from various sources
  useEffect(() => {
    const userDataString = localStorage.getItem("user");
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        if (userData && userData.mobileNumber) {
          setVerifiedPhoneNumber(userData.mobileNumber);
          setValue("mobileNumber", userData.mobileNumber);
          return;
        }
      } catch (error) {
        console.error("Error parsing user data from localStorage:", error);
      }
    }

    const savedPhoneNumber = localStorage.getItem("verifiedPhoneNumber");
    if (savedPhoneNumber) {
      setVerifiedPhoneNumber(savedPhoneNumber);
      setValue("mobileNumber", savedPhoneNumber);
    } else if (isAuthenticated && user?.mobileNumber) {
      setVerifiedPhoneNumber(user.mobileNumber);
      setValue("mobileNumber", user.mobileNumber);
    }
  }, [isAuthenticated, user, setValue]);

  const handleVerifyClick = () => {
    if (mobileNumber && mobileNumber.toString().length === 10) {
      setShowOtpModal(true);
    }
  };

  // Fetch college data from API
  useEffect(() => {
    const fetchCollegesData = async () => {
      setLoading(true);
      try {
        const response = await BaseURL.post(
          "/api/college/college/predicteColleges",
          { examName: exam, pageSize: 10000, pageNo: 0 }
        );
        if (response?.status === 200) {
          const rawData = response?.data?.data;
          setFullCollegesData(rawData);

          // Process and normalize data
          const filteredColleges = rawData.colleges
            .map((college: ICollege) => {
              const collegeYears = college.courseVo
                .map((course) => parseInt(course.year))
                .filter((year) => !isNaN(year));
              const latestYear =
                collegeYears.length > 0
                  ? Math.max(...collegeYears).toString()
                  : "2024";

              // Normalize all courses data
              const allCourses = college.courseVo.map((course) => ({
                ...course,
                gender:
                  course.gender === "" ? "Neutral" : course.gender || "Neutral",
                caste: course.caste || "",
              }));

              // Filter courses by latest year and normalize them
              const filteredCourses = college.courseVo
                .filter((course) => course.year === latestYear)
                .map((course) => ({
                  ...course,
                  gender:
                    course.gender === ""
                      ? "Neutral"
                      : course.gender || "Neutral",
                  caste: course.caste || "",
                }));

              return {
                ...college,
                courseVo: filteredCourses,
                allCourses,
              };
            })
            .filter((college: ICollege) => college.courseVo.length > 0);

          setCollegesData({ ...rawData, colleges: filteredColleges });
        }
      } catch (e) {
        console.log("Error fetching predictor data", e);
      } finally {
        setLoading(false);
      }
    };

    fetchCollegesData();
  }, [exam]);

  // Get available counselling options
  const counsellingOptions = useMemo(() => {
    if (!collegesData?.colleges || collegesData.colleges.length === 0)
      return [];

    const uniqueCounsellings = new Map();

    collegesData.colleges.forEach((college) => {
      college.courseVo.forEach((course) => {
        if (course.counsellingName) {
          uniqueCounsellings.set(course.counsellingName, {
            key: course.counsellingName,
            name: course.counsellingName,
          });
        }
      });
    });

    return Array.from(uniqueCounsellings.values());
  }, [collegesData]);

  // Get available state options for selected counselling
  const stateOptions = useMemo(() => {
    if (!collegesData?.colleges || collegesData.colleges.length === 0)
      return [];

    const uniqueStates = new Map();

    collegesData.colleges.forEach((college) => {
      // Only include states that have colleges matching the selected counselling
      if (
        college.stateName &&
        college.courseVo.some(
          (course) => !counselling || course.counsellingName === counselling
        )
      ) {
        uniqueStates.set(college.stateName, {
          key: college.stateName,
          name: college.stateName,
        });
      }
    });

    return Array.from(uniqueStates.values());
  }, [collegesData, counselling]);

  // Get available caste options for selected counselling and state
  const casteOptions = useMemo(() => {
    if (!collegesData?.colleges || collegesData.colleges.length === 0)
      return [];

    const uniqueCastes = new Map();

    collegesData.colleges.forEach((college) => {
      // Filter by selected state if any
      if (!state || college.stateName === state) {
        college.courseVo.forEach((course) => {
          // Filter by selected counselling if any
          if (
            (!counselling || course.counsellingName === counselling) &&
            course.caste
          ) {
            uniqueCastes.set(course.caste, {
              key: course.caste,
              name: course.caste,
            });
          }
        });
      }
    });

    return Array.from(uniqueCastes.values());
  }, [collegesData, counselling, state]);

  // Get available gender options for selected counselling, state, and caste
  const genderOptions = useMemo(() => {
    if (!collegesData?.colleges || collegesData.colleges.length === 0)
      return [];

    const uniqueGenders = new Map();

    collegesData.colleges.forEach((college) => {
      // Filter by selected state if any
      if (!state || college.stateName === state) {
        college.courseVo.forEach((course) => {
          // Filter by selected counselling and caste if any
          if (
            (!counselling || course.counsellingName === counselling) &&
            (!caste || course.caste === caste)
          ) {
            const genderValue = course.gender || "Neutral";
            uniqueGenders.set(genderValue, {
              key: genderValue,
              name: genderValue,
            });
          }
        });
      }
    });

    return Array.from(uniqueGenders.values());
  }, [collegesData, counselling, state, caste]);

  // Check if we should show the gender field
  // If all gender options are just "Neutral" or there's only one gender option, we'll hide it
  const shouldShowGenderField = useMemo(() => {
    if (genderOptions.length === 0) return false;
    if (genderOptions.length === 1 && genderOptions[0].key === "Neutral") return false;
    return true;
  }, [genderOptions]);

  // Track which fields should be displayed
  const visibleFields = useMemo(
    () => ({
      counselling: counsellingOptions.length > 0,
      state: stateOptions.length > 0,
      caste: casteOptions.length > 0,
      gender: genderOptions.length > 0 && shouldShowGenderField,
    }),
    [counsellingOptions, stateOptions, casteOptions, genderOptions, shouldShowGenderField]
  );

  // Update counselling when options change
  useEffect(() => {
    if (visibleFields.counselling && counsellingOptions.length > 0) {
      // If current counselling is not in available options, reset it
      if (
        counselling &&
        !counsellingOptions.some((opt) => opt.key === counselling)
      ) {
        const defaultOption = counsellingOptions[0].key;
        setCounselling(defaultOption);
        setValue("counselling", defaultOption);
      }
      // If no counselling is selected yet, set a default
      else if (!counselling) {
        const defaultOption = counsellingOptions[0].key;
        setCounselling(defaultOption);
        setValue("counselling", defaultOption);
      }
    } else {
      setCounselling("");
      setValue("counselling", "");
    }
  }, [counsellingOptions, visibleFields.counselling, setValue, counselling]);

  // Update state when options change
  useEffect(() => {
    if (visibleFields.state && stateOptions.length > 0) {
      // If current state is not in available options, reset it
      if (state && !stateOptions.some((opt) => opt.key === state)) {
        const defaultOption = stateOptions[0].key;
        setState(defaultOption);
        setValue("state", defaultOption);
      }
      // If no state is selected yet, set a default
      else if (!state) {
        const defaultOption = stateOptions[0].key;
        setState(defaultOption);
        setValue("state", defaultOption);
      }
    } else {
      setState("");
      setValue("state", "");
    }
  }, [stateOptions, visibleFields.state, setValue, state]);

  // Update caste when options change
  useEffect(() => {
    if (visibleFields.caste && casteOptions.length > 0) {
      // If current caste is not in available options, reset it
      if (caste && !casteOptions.some((opt) => opt.key === caste)) {
        const defaultOption = casteOptions[0].key;
        setCaste(defaultOption);
        setValue("caste", defaultOption);
      }
      // If no caste is selected yet, set a default
      else if (!caste) {
        const defaultOption = casteOptions[0].key;
        setCaste(defaultOption);
        setValue("caste", defaultOption);
      }
    } else {
      setCaste("");
      setValue("caste", "");
    }
  }, [casteOptions, visibleFields.caste, setValue, caste]);

  // Update gender when options change
  useEffect(() => {
    // Always set the gender to "Neutral" by default if field is not visible
    if (!visibleFields.gender) {
      setGender("Neutral");
      setValue("gender", "Neutral");
      return;
    }

    if (visibleFields.gender && genderOptions.length > 0) {
      // If current gender is not in available options, reset it
      if (gender && !genderOptions.some((opt) => opt.key === gender)) {
        const defaultOption = genderOptions[0].key;
        setGender(defaultOption);
        setValue("gender", defaultOption);
      }
      // If no gender is selected yet, set a default
      else if (!gender) {
        const defaultOption = genderOptions[0].key;
        setGender(defaultOption);
        setValue("gender", defaultOption);
      }
    } else {
      setGender("Neutral");
      setValue("gender", "Neutral");
    }
  }, [genderOptions, visibleFields.gender, setValue, gender]);

  // Handle form submission
  const onPreSubmit = (data: FormData) => {
    setFormDataToSubmit(data);
    if (verifiedPhoneNumber) {
      const finalFormData = {
        ...data,
        mobileNumber: verifiedPhoneNumber,
        gender: data.gender === "" ? "Neutral" : data.gender,
      } as FormData;

      const queryParams = Object.keys(finalFormData)
        .filter((key) => !["rank", "mobileNumber"].includes(key))
        .filter(
          (key) => {
            // Always include gender as "Neutral" in the form data
            if (key === "gender" && !visibleFields.gender) {
              return false; // Don't include in URL if field isn't shown
            }
            return finalFormData[key] !== "" && finalFormData[key] !== undefined;
          }
        )
        .map((key) => `${key}=${finalFormData[key]}`)
        .join("&");

      navigate(
        `/college-predictor-result/${exam}/${finalFormData.rank}?${queryParams}`,
        {
          state: {
            collegesData,
            formData: finalFormData,
          },
        }
      );
    }
  };

  // Handle OTP verification success
  const handleSuccessfulVerification = (userData: any) => {
    if (!formDataToSubmit) {
      return;
    }

    const mobileToUse = userData?.mobileNumber || formDataToSubmit.mobileNumber;
    setVerifiedPhoneNumber(mobileToUse);

    const finalFormData = {
      ...formDataToSubmit,
      mobileNumber: mobileToUse,
      gender: visibleFields.gender
        ? (formDataToSubmit.gender === "" ? "Neutral" : formDataToSubmit.gender)
        : "Neutral",
    } as FormData;

    const queryParams = Object.keys(finalFormData)
      .filter((key) => !["rank", "mobileNumber"].includes(key))
      .filter(
        (key) => {
          // Don't include gender in URL params if field isn't shown
          if (key === "gender" && !visibleFields.gender) {
            return false; 
          }
          return finalFormData[key] !== "" && finalFormData[key] !== undefined;
        }
      )
      .map((key) => `${key}=${finalFormData[key]}`)
      .join("&");

    navigate(
      `/college-predictor-result/${exam}/${finalFormData.rank}?${queryParams}`,
      {
        state: {
          collegesData,
          formData: finalFormData,
        },
      }
    );
  };

  // Check if form is valid for submission
  const isFormValid =
    !!verifiedPhoneNumber &&
    (!visibleFields.counselling || !!counselling) &&
    (!visibleFields.state || !!state) &&
    (!visibleFields.caste || !!caste) &&
    (!!gender); // Always consider gender valid since we set a default value

  return (
    <>
      <section className="py-9 bg-[#F4F4F4] text-black">
        <div className="container mx-auto">
          <div className="shadow-xl bg-white pb-12 md:max-w-[630px] mx-auto">
            <div
              style={{ width: "100%", height: "100%", background: "#00CC99" }}
              className="md:py-3"
            >
              <h5 className="text-xl font-bold text-center">
                <span className="text-white">{data?.bannerInfo?.title}</span>
              </h5>
            </div>

            {loading ? (
              <div className="flex justify-center items-center p-12">
                <div className="loader animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue"></div>
              </div>
            ) : (
              <form
                className="mt-6 flex flex-col gap-4 px-8"
                onSubmit={handleSubmit(onPreSubmit)}
              >
                <div>
                  <label className="text-xs text-grey">Enter Your Rank</label>
                  <input
                    {...register("rank", {
                      required: "Rank is required",
                      pattern: {
                        value: /^\d+$/,
                        message: "Invalid rank",
                      },
                    })}
                    className="w-full mt-2 py-1 rounded border border-grey-3 text-base px-2"
                  />
                  <p className="text-red-500 text-xs mt-1">
                    {errors.rank?.message}
                  </p>
                </div>

                {/* Dynamic filters */}
                {Object.keys(FilterType)
                  .filter(
                    (fieldKey) =>
                      visibleFields[fieldKey as keyof typeof visibleFields]
                  )
                  .map((fieldKey) => {
                    switch (FilterType[fieldKey]) {
                      case "dropdown":
                        const optionsMap: Record<
                          string,
                          { key: string; name: string }[]
                        > = {
                          counselling: counsellingOptions,
                          state: stateOptions,
                          caste: casteOptions,
                        };
                        const options = optionsMap[fieldKey] || [];

                        return options.length > 0 ? (
                          <DropDown
                            key={fieldKey}
                            fieldName={
                              fieldKey.charAt(0).toUpperCase() +
                              fieldKey.slice(1)
                            }
                            field={fieldKey}
                            options={options}
                            register={register}
                            errors={errors[fieldKey]?.message || ""}
                            onChange={(value: string) => {
                              if (fieldKey === "counselling") {
                                setCounselling(value);
                                setState("");
                                setCaste("");
                                setGender("");
                                setValue("state", "");
                                setValue("caste", "");
                                setValue("gender", "");
                              } else if (fieldKey === "state") {
                                setState(value);
                                setCaste("");
                                setGender("");
                                setValue("caste", "");
                                setValue("gender", "");
                              } else if (fieldKey === "caste") {
                                setCaste(value);
                                setGender("");
                                setValue("gender", "");
                              }
                            }}
                            isRequired={true}
                            selectedValue={
                              fieldKey === "counselling"
                                ? counselling
                                : fieldKey === "state"
                                ? state
                                : fieldKey === "caste"
                                ? caste
                                : undefined
                            }
                          />
                        ) : null;
                      case "radio":
                        return genderOptions.length > 0 ? (
                          <Radio
                            key={fieldKey}
                            fieldName="Gender"
                            field={fieldKey}
                            options={genderOptions}
                            register={register}
                            errors={errors[fieldKey]?.message || ""}
                            onChange={(value: string) => {
                              setGender(value);
                            }}
                            isRequired={true}
                            selectedValue={gender}
                          />
                        ) : null;
                      default:
                        return null;
                    }
                  })}

                <span className="text-sm font-semibold text-green">
                  College predictor report will be sent to you on:
                </span>

                <div>
                  <label className="text-xs text-grey">
                    Enter Your Mobile Number
                  </label>
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      {...register("mobileNumber", {
                        required: "Mobile number is required",
                        pattern: {
                          value: /^[0-9]{10}$/,
                          message: "Invalid mobile number",
                        },
                      })}
                      className={`flex-1 py-1 rounded border border-grey-3 px-2 ${
                        verifiedPhoneNumber ? "bg-gray-100" : ""
                      }`}
                      disabled={!!verifiedPhoneNumber}
                    />
                    {!verifiedPhoneNumber && (
                      <button
                        type="button"
                        onClick={handleVerifyClick}
                        disabled={
                          !mobileNumber || mobileNumber.toString().length !== 10
                        }
                        className={`px-3 py-1 rounded text-white text-sm ${
                          !mobileNumber || mobileNumber.toString().length !== 10
                            ? "bg-gray-300 cursor-not-allowed"
                            : "bg-blue hover:bg-indigo-700"
                        }`}
                      >
                        Verify
                      </button>
                    )}
                    {verifiedPhoneNumber && (
                      <span className="bg-green-100 text-green-700 px-3 py-1 rounded-md text-sm">
                        Verified ✓
                      </span>
                    )}
                  </div>
                  <p className="text-red-500 text-xs mt-1">
                    {errors.mobileNumber?.message}
                  </p>
                  {verifiedPhoneNumber && (
                    <p className="text-xs text-green-600 mt-1">
                      Your mobile number has been verified.
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={!isFormValid}
                  className={`text-base font-semibold text-white px-12 py-2 mx-auto rounded ${
                    !isFormValid
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-blue hover:bg-indigo-700"
                  }`}
                >
                  Predictor
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <OTPVerificationModal
        isOpen={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        onSuccess={(userData) => {
          setShowOtpModal(false);
          if (userData?.mobileNumber) {
            setVerifiedPhoneNumber(userData.mobileNumber);
            setValue("mobileNumber", userData.mobileNumber);
          }
          if (formDataToSubmit) {
            handleSuccessfulVerification(userData);
          }
        }}
        initialMobileNumber={mobileNumber?.toString() || ""}
        isUserLoggedIn={isAuthenticated}
      />
    </>
  );
};

export default PredictionForm;

const DropDown = ({
  options,
  fieldName,
  register,
  field,
  errors,
  onChange,
  isRequired,
  selectedValue,
}: {
  options: { key: string; name: string }[];
  fieldName: string;
  field: string;
  register: UseFormRegister<FormData>;
  errors: string;
  onChange?: (value: string) => void;
  isRequired: boolean;
  selectedValue?: string;
}) => {
  return (
    <div>
      <label className="text-xs text-grey">{fieldName}</label>
      <select
        className="w-full mt-2 py-1 px-2 rounded border border-grey-3 text-sm"
        {...register(field, {
          required: isRequired ? `${fieldName} is required` : false,
        })}
        onChange={(e) => {
          register(field).onChange(e);
          if (onChange) onChange(e.target.value);
        }}
        value={selectedValue || ""}
      >
        {options.length > 1 && <option value="">Select {fieldName}</option>}
        {options.map((option) => (
          <option key={option.key} value={option.key} className="text-sm">
            {option.name}
          </option>
        ))}
      </select>
      <p className="text-red-500 text-xs mt-1">{errors}</p>
    </div>
  );
};

const Radio = ({
  options,
  fieldName,
  field,
  register,
  errors,
  onChange,
  isRequired,
  selectedValue,
}: {
  options: { key: string; name: string }[];
  fieldName: string;
  field: string;
  register: UseFormRegister<FormData>;
  errors: string;
  onChange?: (value: string) => void;
  isRequired: boolean;
  selectedValue?: string;
}) => {
  return (
    <div>
      <label className="text-xs text-grey">{fieldName}</label>
      <div className="flex gap-4 mt-2">
        {options.map((option) => (
          <label key={option.key} className="flex gap-2 items-center text-sm">
            <input
              type="radio"
              value={option.key}
              className="text-sm"
              checked={selectedValue === option.key}
              {...register(field, {
                required: isRequired ? `${fieldName} is required` : false,
              })}
              onChange={(e) => {
                register(field).onChange(e);
                if (onChange) onChange(e.target.value);
              }}
            />
            <span>{option.name}</span>
          </label>
        ))}
      </div>
      <p className="text-red-500 text-xs mt-1">{errors}</p>
    </div>
  );
};
